import React, { FC, useState } from 'react';
import { EnumButtonSize, EnumTheme, WLabel } from 'constants/enums';
import styled from 'styled-components';
import { isNil } from 'ramda';
import ThemeButton from 'Components/ThemedButton';
import { colors } from 'constants/colors';
import { mediaSmDown } from 'theme/helpers/css';
import { selectByWL } from 'utils/wLabel';
import { FontSize, FontWeight } from 'theme';
import Text from 'Components/Typography/Text';
import ChevronDownMiniIcon from 'Components/Icon/ChevronDownMiniIcon';
import { addStyleIfPropTruly, addXsStyleIfPropTruly } from 'utils/styledComponents';
import { shadows } from 'constants/shadows';
import Flex from 'Components/Grid/Flex';
import { Transitions } from 'theme';
import Dropdown from 'Components/Dropdown';

export const DROPDOWN_SELECT_CHEVRON_TEST_ID = 'DROPDOWN_SELECT_CHEVRON_TEST_ID';
export const DROPDOWN_SELECT_BUTTON_TEST_ID = 'DROPDOWN_SELECT_BUTTON_TEST_ID';
export const DROPDOWN_SELECT_CONTAINER_TEST_ID = 'DROPDOWN_SELECT_CONTAINER_TEST_ID';
export const DROPDOWN_SELECT_CONTENT_TEST_ID = 'DROPDOWN_SELECT_CONTENT_TEST_ID';
export const DROPDOWN_SELECT_ITEM_TEST_ID = 'DROPDOWN_SELECT_ITEM_TEST_ID';

interface IDropdownSelectChevron {
  isOpen?: boolean;
}
interface IDropdownSelectContainer {
  withMb: boolean;
}

const Container = styled.div<IDropdownSelectContainer>`
  position: relative;
  max-width: 300px;

  ${addStyleIfPropTruly('withMb', ` margin-bottom: 20px;`)}
  ${addXsStyleIfPropTruly('withMb', 'margin-bottom: 8px;')}

  ${mediaSmDown`
    max-width: 100%;
  `}
`;

const getPaddingBySize = (size: EnumButtonSize) => {
  switch (size) {
    case EnumButtonSize.small:
      return '32px';
    default:
      return '46px';
  }
};

export const Button = styled(ThemeButton)`
  padding-left: 12px;
  padding-right: ${({ size }) => getPaddingBySize(size)};
  display: flex;
  user-select: none;
  width: 100%;

  ${({ isOpened }) =>
    isOpened &&
    `
    color: ${colors.darkGrey};
    background-color: ${colors.silverFour};
  `};

  i {
    margin-right: 5px;
    color: currentColor;
  }

  div {
    text-align: left;
    justify-content: flex-start;
  }
`;

const wlButtonTextFontWeight = selectByWL({ [WLabel.Globus]: FontWeight.semiBold });
const wlButtonTextFontSizeNormal = selectByWL({ [WLabel.Default]: FontSize.s, [WLabel.Globus]: FontSize.xs });
const wlButtonTextFontSizeSmall = selectByWL({ [WLabel.Default]: FontSize.xs, [WLabel.Globus]: FontSize.xs });

const getFontSizeByButtonSize = (size: EnumButtonSize): FontSize => {
  switch (size) {
    case EnumButtonSize.small:
      return wlButtonTextFontSizeSmall;
    default:
      return wlButtonTextFontSizeNormal;
  }
};

const Chevron = styled.i<IDropdownSelectChevron>`
  position: absolute;
  right: 8px;
  top: 50%;
  transition: transform 180ms ease-in 16ms;

  ${({ isOpen }) =>
    isOpen ? `transform: translateY(-50%) rotate(-180deg);` : `transform: translateY(-50%) rotate(-0deg);`};
`;

const List = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  min-width: 100%;
  width: 100%;
  z-index: 3;
  max-height: 220px;
  top: 100%;
  border-radius: 4px;
  background-color: ${colors.white};
  box-shadow: ${shadows.boxShadow};
`;

const Item = styled(Flex).attrs({
  middle: true,
})<{ selected: boolean }>`
  font-size: 14px;
  height: 40px;
  padding: 4px 12px;
  background-color: ${colors.white};
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  transition: ${Transitions.bg};

  ${addStyleIfPropTruly('selected', `display: none;`)}

  ${mediaSmDown`
    height: 30px;
    font-size: 12px;
  `}

  &:hover {
    background-color: ${colors.paleGrey};
  }
`;

export interface IDropdownSelectProps {
  use?: EnumTheme;
  className?: string;
  classNameContent?: string;
  list: string[];
  disabled?: boolean;
  selectedItem?: number;
  onSelectDropdownItem?: (index: number) => void;
  testId?: string;
  testIdButton?: string;
  testIdContent?: string;
  testIditem?: string;
  size?: EnumButtonSize;
  withMb?: boolean;
  withChevron?: boolean;
}

export const DropdownSelect: FC<IDropdownSelectProps> = ({
  use = EnumTheme.quaternary,
  className,
  classNameContent,
  list,
  disabled,
  selectedItem,
  onSelectDropdownItem,
  testId = DROPDOWN_SELECT_CONTAINER_TEST_ID,
  testIdButton = DROPDOWN_SELECT_BUTTON_TEST_ID,
  testIdContent = DROPDOWN_SELECT_CONTENT_TEST_ID,
  testIditem = DROPDOWN_SELECT_ITEM_TEST_ID,
  size = EnumButtonSize.normal,
  withChevron = false,
  withMb = false,
}: IDropdownSelectProps) => {
  const [isOpen, setOpen] = useState<boolean>(false);

  const itemClickHandler = (index: number) => {
    onSelectDropdownItem && onSelectDropdownItem(index);
    setOpen(false);
  };

  const toggleHandler = () => setOpen(!isOpen);
  const closeHandler = () => setOpen(false);

  const toggle = () => {
    return (
      <Button isOpened={isOpen} disabled={disabled} use={use} size={size} testId={testIdButton}>
        <Text overflow fontWeight={wlButtonTextFontWeight} size={getFontSizeByButtonSize(size)}>
          {!isNil(selectedItem) && list[selectedItem]}
        </Text>
        {list.length > 1 && withChevron && (
          <Chevron isOpen={isOpen} data-testid={DROPDOWN_SELECT_CHEVRON_TEST_ID}>
            <ChevronDownMiniIcon color={colors.pinkishGrey} />
          </Chevron>
        )}
      </Button>
    );
  };

  return (
    <Container withMb={withMb} className={className} data-testid={testId}>
      <Dropdown fluid isOpen={isOpen} toggle={toggle} onToggle={toggleHandler} onClose={closeHandler}>
        <List className={classNameContent} data-testid={testIdContent}>
          {list.map((item, i) => (
            <Item selected={i === selectedItem} onClick={() => itemClickHandler(i)} key={item} data-testid={testIditem}>
              <Text overflow fontWeight={wlButtonTextFontWeight} size={getFontSizeByButtonSize(size)}>
                {item}
              </Text>
            </Item>
          ))}
        </List>
      </Dropdown>
    </Container>
  );
};

export default DropdownSelect;
